import React, { useState, useEffect } from "react";
import CloseIcon from "../../images/CloseIcon";
import {
  getOne,
  confirm,
  cancel,
  PayrollPayment,
  PayrollPaymentDetailRow,
} from "../../services/payrollPayments.service";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import TagStatus from "../../components/TagStatus";

const STATUS_DRAFT = 1;
const STATUS_COMPLETED = 2;
const STATUS_CANCELLED = 3;

const statusLabel = (statusId: number): string => {
  switch (statusId) {
    case STATUS_DRAFT:
      return "Draft";
    case STATUS_COMPLETED:
      return "Completed";
    case STATUS_CANCELLED:
      return "Cancelled";
    default:
      return "—";
  }
};

const formatDate = (dateString: string) => {
  if (!dateString) return "—";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
};

interface PayrollPaymentModalProps {
  batchId: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const PayrollPaymentModal: React.FC<PayrollPaymentModalProps> = ({
  batchId,
  onClose,
  onSuccess,
}) => {
  const [payment, setPayment] = useState<PayrollPayment | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const language = (window as any).languageContext?.language;

  useEffect(() => {
    if (!batchId) {
      setPayment(null);
      return;
    }
    setLoading(true);
    getOne(batchId)
      .then((data) => setPayment(data))
      .catch((err) => {
        showApiError(err, { language, titleFallback: "Failed to load payroll payment" });
        setPayment(null);
      })
      .finally(() => setLoading(false));
  }, [batchId]);

  const handleConfirm = async () => {
    if (!batchId || payment?.statusId !== STATUS_DRAFT) return;
    setActionLoading(true);
    try {
      const updated = await confirm(batchId);
      setPayment(updated);
      showToast({
        message: "Payroll payment confirmed. GL records have been created.",
        type: "success",
      });
      onSuccess?.();
    } catch (err: any) {
      showApiError(err, { language, titleFallback: "Failed to confirm" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!batchId || payment?.statusId !== STATUS_DRAFT) return;
    setActionLoading(true);
    try {
      const updated = await cancel(batchId);
      setPayment(updated);
      showToast({ message: "Payroll payment cancelled.", type: "success" });
      onSuccess?.();
    } catch (err: any) {
      showApiError(err, { language, titleFallback: "Failed to cancel" });
    } finally {
      setActionLoading(false);
    }
  };

  const isDraft = payment?.statusId === STATUS_DRAFT;
  const details = (payment?.details ?? []).filter((d) => !d.isDeleted);

  if (!batchId) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[200]"
      style={{ backgroundColor: "rgba(34, 37, 37, 0.32)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-lg font-inter mx-4 flex flex-col"
        style={{ maxWidth: "720px", width: "100%", maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#EBF0F0]">
          <div>
            <p className="text-[20px] font-semibold leading-[24px]" style={{ color: "#222525" }}>
              {loading
                ? "Loading..."
                : payment?.description || payment?.sourceFileName || "Payroll payment"}
            </p>
            {payment && (
              <p className="text-[14px] mt-1" style={{ color: "#4A5353" }}>
                {formatDate(payment.paymentDate)} · Period {payment.payrollPeriodYear}-
                {String(payment.payrollPeriodMonth).padStart(2, "0")} · {payment.lineCount} rows ·
                Total {Number(payment.totalAmount).toFixed(2)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {payment && <TagStatus value={statusLabel(payment.statusId)} />}
            <div
              className="cursor-pointer p-2 rounded-full hover:bg-[#EBF0F0]"
              onClick={onClose}
            >
              <CloseIcon />
            </div>
          </div>
        </div>

        {isDraft && payment && (
          <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-[#EBF0F0]">
            <button
              type="button"
              onClick={handleCancel}
              disabled={actionLoading}
              className="px-5 py-2.5 rounded-full text-[14px] font-medium border border-[#EBF0F0]"
              style={{ color: "#4A5353" }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={actionLoading}
              className="px-5 py-2.5 rounded-full text-[14px] font-medium border border-[#EBF0F0]"
              style={{ color: "#6A7878", backgroundColor: "#F9F9F9" }}
              title="Already saved as draft"
            >
              Save as draft
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={actionLoading}
              className="px-5 py-2.5 rounded-full text-[14px] font-medium text-white"
              style={{ backgroundColor: "#0F7679" }}
            >
              {actionLoading ? "Confirming..." : "Confirm"}
            </button>
          </div>
        )}

        <div className="overflow-auto flex-1 p-5">
          {loading ? (
            <p className="text-[14px]" style={{ color: "#4A5353" }}>
              Loading...
            </p>
          ) : (
            <div className="border border-[#EBF0F0] rounded-xl overflow-hidden">
              <table className="w-full text-[14px]" style={{ color: "#222525" }}>
                <thead>
                  <tr style={{ backgroundColor: "#F5F7F7" }}>
                    <th className="text-left py-3 px-4 font-medium">Row</th>
                    <th className="text-left py-3 px-4 font-medium">Name</th>
                    <th className="text-left py-3 px-4 font-medium">Bank account</th>
                    <th className="text-right py-3 px-4 font-medium">Amount</th>
                    <th className="text-left py-3 px-4 font-medium">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {details.map((d: PayrollPaymentDetailRow) => (
                    <tr key={d.id} className="border-t border-[#EBF0F0]">
                      <td className="py-3 px-4">{d.rowNo}</td>
                      <td className="py-3 px-4">
                        {[d.firstName, d.lastName].filter(Boolean).join(" ") || "—"}
                      </td>
                      <td className="py-3 px-4">{d.bankAccountNumber || "—"}</td>
                      <td className="py-3 px-4 text-right">
                        {Number(d.amount).toFixed(2)}
                      </td>
                      <td className="py-3 px-4">{d.description || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PayrollPaymentModal.displayName = "PayrollPaymentModal";
export default PayrollPaymentModal;
